import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import type { SystemToast } from "@/stores/notification-store";
import type { Project } from "@/api/spaces";
import { abandonOnboardingOnUserSpaceSwitch } from "@/desktop/onboarding/abandon-onboarding-on-switch";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

export interface ProjectIdentity {
  projectId: string;
  projectName?: string;
}

/** Select an owned project, refreshing the local project list when necessary. */
export async function selectProjectByIdentity(
  target: ProjectIdentity,
  dependencies: {
    projectStore?: typeof useProjectStore;
    addSystemToast?: (toast: SystemToast) => void;
    shouldContinue?: () => boolean;
  } = {},
): Promise<boolean> {
  const projectStore = dependencies.projectStore ?? useProjectStore;
  const addSystemToast =
    dependencies.addSystemToast ??
    ((toast: SystemToast) =>
      useNotificationStore.getState().addSystemToast(toast));
  const shouldContinue = dependencies.shouldContinue ?? (() => true);
  if (!shouldContinue()) return false;
  let state = projectStore.getState();
  if (state.currentProject?.id === target.projectId) {
    return true;
  }

  let project = state.projects.find((item) => item.id === target.projectId);
  if (!project) {
    await state.fetchAllProjects();
    if (!shouldContinue()) return false;
    state = projectStore.getState();
    project = state.projects.find((item) => item.id === target.projectId);
  }
  if (!project) {
    addSystemToast({
      id: `project-switch-failed-${target.projectId}`,
      level: "error",
      title: translate(libChatMessages, "projectSwitchFailedTitle"),
      description: target.projectName
        ? translate(libChatMessages, "projectNotLocal", {
            name: target.projectName,
          })
        : translate(libChatMessages, "selectedProjectNotLocal"),
    });
    return false;
  }
  if (!shouldContinue()) return false;
  projectStore.getState().selectProject(project);
  return true;
}

/** A switch the user asked for: ends a mid-flow onboarding, then fits the new canvas. */
export function selectProjectFromUser(project: Project): void {
  abandonOnboardingOnUserSpaceSwitch(project.id);
  useProjectStore.getState().selectProject(project, { fitOnLoad: true });
}

/** A project the user asked for: create it, switch to it, or tell them it failed. */
export async function createProjectFromUser(
  toastIdPrefix: string,
): Promise<Project | null> {
  try {
    const project = await useProjectStore
      .getState()
      .createProjectWithDefaults();
    selectProjectFromUser(project);
    return project;
  } catch {
    useNotificationStore.getState().addSystemToast({
      id: `${toastIdPrefix}-${Date.now()}`,
      title: translate(libChatMessages, "projectCreateFailed"),
      description: translate(libChatMessages, "tryAgain"),
      level: "error",
    });
    return null;
  }
}
